import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import analyticsService from '../../services/analyticsService';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import { 
  Map, 
  Mail, 
  Github, 
  Twitter, 
  Instagram, 
  Youtube, 
  Facebook,
  Send,
  Users,
  User,
  Globe,
  Shield,
  Smartphone,
  Download,
  ArrowUp,
  BookOpen,
  FileText,
  Code,
  HelpCircle,
  Video
} from 'lucide-react';

const Footer = () => {
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const device = useDeviceDetection();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleComingSoonClick = (e, featureName) => {
    // Предотвращаем переход по ссылке
    e.preventDefault();
    
    // Показываем уведомление
    toast.info(t('footer.comingSoon'), {
      position: 'bottom-center',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
    
    // Отслеживаем клик в аналитике
    analyticsService.trackEvent('footer_coming_soon_click', {
      feature: featureName,
      location: 'footer'
    });
  };

  return (
    <footer className="app-footer bg-gray-900 text-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-6">
              <Map className="footer-logo-icon h-8 w-8 text-blue-400" />
              <span className="footer-brand-text text-2xl font-bold">TravelMapPlatform</span>
            </div>
            <p className="footer-desc text-gray-400 mb-6 max-w-md leading-relaxed">
              {t('app.description')}
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-4 mb-6">
              <a
                href="https://www.facebook.com/travelmapplatform"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-btn w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://t.me/TravelMapPlatform"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-btn w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-all duration-300"
                aria-label="Telegram"
              >
                <Send className="h-5 w-5" />
              </a>
              <a
                href="#"
                className="footer-social-btn w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-all duration-300"
                aria-label="GitHub"
              >
                <Github className="h-5 w-5" />
              </a>
              <a
                href="#"
                className="footer-social-btn w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-all duration-300"
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="#"
                className="footer-social-btn w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="#"
                className="footer-social-btn w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-all duration-300"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-4 text-sm text-gray-400">
              <div className="flex items-center">
                <Shield className="footer-feature-icon h-4 w-4 mr-2 text-green-400" />
                <span>Secure</span>
              </div>
              <div className="flex items-center">
                <Smartphone className="footer-feature-icon h-4 w-4 mr-2 text-blue-400" />
                <span>Mobile Ready</span>
              </div>
              <div className="flex items-center">
                <Download className="footer-feature-icon h-4 w-4 mr-2 text-blue-400" />
                <span>PWA</span>
              </div>
            </div>
          </div>

          {/* Platform */}
          <div>
            <h3 className={`footer-heading text-lg font-semibold mb-6 ${device.isMobile ? 'text-center' : ''}`}>{t('footer.platform')}</h3>
            <ul className={device.isMobile ? 'flex flex-nowrap justify-center gap-2' : 'space-y-3'}>
              <li className={device.isMobile ? 'flex-shrink-0' : ''}>
                <Link to="/map" onClick={scrollToTop} className="footer-link text-gray-400 hover:text-white transition-colors flex items-center whitespace-nowrap">
                  <Map className="h-4 w-4 mr-2" />
                  {t('navigation.map')}
                </Link>
              </li>
              <li className={device.isMobile ? 'flex-shrink-0' : ''}>
                <Link 
                  to="/feed" 
                  onClick={scrollToTop}
                  onMouseEnter={() => {
                    // Preload FeedPage chunk при наведении
                    import('../../pages/FeedPage/FeedPage');
                  }}
                  className="footer-link text-gray-400 hover:text-white transition-colors flex items-center whitespace-nowrap"
                >
                  <Globe className="h-4 w-4 mr-2" />
                  {t('navigation.feed')}
                </Link>
              </li>
              <li className={device.isMobile ? 'flex-shrink-0' : ''}>
                <Link to="/groups" onClick={scrollToTop} className="footer-link text-gray-400 hover:text-white transition-colors flex items-center whitespace-nowrap">
                  <Users className="h-4 w-4 mr-2" />
                  {t('navigation.groups')}
                </Link>
              </li>
              <li className={device.isMobile ? 'flex-shrink-0' : ''}>
                <Link to={`/profile/${user?.id || ''}`} onClick={scrollToTop} className="footer-link text-gray-400 hover:text-white transition-colors flex items-center whitespace-nowrap">
                  <User className="h-4 w-4 mr-2" />
                  {t('navigation.profile')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Community */}
          <div>
            <h3 className={`footer-heading text-lg font-semibold mb-6 ${device.isMobile ? 'text-center' : ''}`}>{t('footer.community')}</h3>
            <ul className={device.isMobile ? 'flex flex-nowrap justify-center gap-2' : 'space-y-3'}>
              {!user && (
                <li className={device.isMobile ? 'flex-shrink-0' : ''}>
                  <Link to="/register" className="footer-link text-gray-400 hover:text-white transition-colors whitespace-nowrap">
                    Join Community
                  </Link>
                </li>
              )}
              <li className={device.isMobile ? 'flex-shrink-0' : ''}>
                <a 
                  href="#" 
                  onClick={(e) => handleComingSoonClick(e, 'travelStories')}
                  className="footer-link text-gray-400 hover:text-white transition-colors flex items-center cursor-pointer whitespace-nowrap"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  {t('footer.travelStories')}
                </a>
              </li>
              
              <li className={device.isMobile ? 'flex-shrink-0' : ''}>
                <a 
                  href="#" 
                  onClick={(e) => handleComingSoonClick(e, 'blog')}
                  className="footer-link text-gray-400 hover:text-white transition-colors flex items-center cursor-pointer whitespace-nowrap"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {t('footer.blog')}
                </a>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className={`footer-heading text-lg font-semibold mb-6 ${device.isMobile ? 'text-center' : ''}`}>{t('footer.support')}</h3>
            <ul className={device.isMobile ? 'flex flex-wrap justify-center gap-2' : 'space-y-3'}>
              <li className={device.isMobile ? 'inline-block' : ''}>
                <Link to="/about" onClick={scrollToTop} className="footer-link text-gray-400 hover:text-white transition-colors flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  {t('footer.about')}
                </Link>
              </li>
              <li className={device.isMobile ? 'inline-block' : ''}>
                <Link to="/for-creators" onClick={scrollToTop} className="footer-link text-gray-400 hover:text-white transition-colors flex items-center">
                  <Video className="h-4 w-4 mr-2" />
                  {t('footer.forCreators')}
                </Link>
              </li>
              <li className={device.isMobile ? 'inline-block' : ''}>
                <Link to="/faq" onClick={scrollToTop} className="footer-link text-gray-400 hover:text-white transition-colors flex items-center">
                  <HelpCircle className="h-4 w-4 mr-2" />
                  {t('footer.faq')}
                </Link>
              </li>
              <li className={device.isMobile ? 'inline-block' : ''}>
                <Link to="/contact-us" onClick={scrollToTop} className="footer-link text-gray-400 hover:text-white transition-colors flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  {t('footer.contactUs')}
                </Link>
              </li>
              <li className={device.isMobile ? 'inline-block' : ''}>
                <Link to="/guide" onClick={scrollToTop} className="footer-link text-gray-400 hover:text-white transition-colors flex items-center">
                  <BookOpen className="h-4 w-4 mr-2" />
                  {t('footer.guide')}
                </Link>
              </li>
              <li className={device.isMobile ? 'basis-full flex justify-center' : ''}>
                <a 
                  href="#" 
                  onClick={(e) => handleComingSoonClick(e, 'apiDocumentation')}
                  className="footer-link text-gray-400 hover:text-white transition-colors flex items-center cursor-pointer"
                >
                  <Code className="h-4 w-4 mr-2" />
                  {t('footer.apiDocumentation')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="footer-bottom-border border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col lg:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-8">
              <p className="footer-bottom-text text-gray-400 text-sm">
                © 2026 TravelMapPlatform. All rights reserved.
              </p>
              <div className="flex space-x-6">
                <Link to="/privacy" onClick={scrollToTop} className="footer-link text-gray-400 hover:text-white transition-colors text-sm">
                  Privacy Policy
                </Link>
                <Link to="/terms" onClick={scrollToTop} className="footer-link text-gray-400 hover:text-white transition-colors text-sm">
                  Terms of Service
                </Link>
                <Link to="/cookies" onClick={scrollToTop} className="footer-link text-gray-400 hover:text-white transition-colors text-sm">
                  Cookie Policy
                </Link>
              </div>
            </div>
            
            <div className="flex items-center space-x-6 mt-4 lg:mt-0">
              <div className="flex items-center space-x-2">
                <span className="footer-lang-label text-gray-400 text-sm">Available in:</span>
                <span className="footer-lang-value text-blue-400 text-sm font-medium">English</span>
                <span className="text-gray-500">•</span>
                <span className="footer-lang-value text-blue-400 text-sm font-medium">Русский</span>
              </div>
              
              {/* Back to Top Button */}
              <button
                onClick={scrollToTop}
                className="footer-back-top w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-all duration-300"
                aria-label="Back to top"
              >
                <ArrowUp className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;